import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import DriverDocsManager from '../components/DriverDocsManager';
import LeaseStatementBuilder from '../components/LeaseStatementBuilder';
import {
  FolderOpen, Upload, Plus, CheckCircle, AlertTriangle, Clock,
  FileText, Search, Download, Send, MessageCircle, Mail, Smartphone,
  Globe, ChevronDown, ChevronUp, X, Eye, BookOpen,
} from 'lucide-react';

const CATEGORIES = ['All', 'WHS', 'Contracts', 'Compliance', 'Transport', 'Training'];

const STATUS_INFO = {
  current: { badge: 'badge-green', icon: CheckCircle, color: 'var(--hp-green)', label: 'Current' },
  review:  { badge: 'badge-gold',  icon: Clock,       color: 'var(--hp-gold)',  label: 'Due for Review' },
  expired: { badge: 'badge-red',   icon: AlertTriangle,color: 'var(--hp-red)',  label: 'Expired' },
};

function daysUntil(dateStr) {
  return Math.floor((new Date(dateStr) - new Date()) / 86400000);
}

// ── Driver Portal Guides ──────────────────────────────────────────────────────
const DRIVER_GUIDES = [
  { lang: 'English',  flag: '🇦🇺', file: '/docs/driver-guide-en.pdf', code: 'en' },
  { lang: 'Spanish',  flag: '🇪🇸', file: '/docs/driver-guide-es.pdf', code: 'es' },
  { lang: 'Mandarin', flag: '🇨🇳', file: '/docs/driver-guide-zh.pdf', code: 'zh' },
  { lang: 'Mongolian',flag: '🇲🇳', file: '/docs/driver-guide-mn.pdf', code: 'mn' },
];

// ── Send Modal ────────────────────────────────────────────────────────────────
function SendModal({ guide, drivers, onClose, addNotification }) {
  const [selected, setSelected]   = useState([]);
  const [channel, setChannel]     = useState('sms');
  const [sending, setSending]     = useState(false);
  const [filterText, setFilterText] = useState('');

  const filteredDrivers = (drivers || []).filter(d =>
    !filterText || d.name?.toLowerCase().includes(filterText.toLowerCase()) ||
    d.loginName?.toLowerCase().includes(filterText.toLowerCase())
  );

  const toggle = (login) =>
    setSelected(s => s.includes(login) ? s.filter(x => x !== login) : [...s, login]);

  const selectAll = () => setSelected(filteredDrivers.map(d => d.loginName));
  const clearAll  = () => setSelected([]);

  const send = async () => {
    if (!selected.length) { addNotification('Select at least one driver', 'error'); return; }
    setSending(true);
    const guideUrl = `https://hollypaul.com${guide.file}`;
    const message  = `HOLLYPAUL: Your Driver Portal guide (${guide.lang}) is ready. Download here: ${guideUrl}`;

    try {
      const targets = (drivers || []).filter(d => selected.includes(d.loginName));
      let ok = 0;
      for (const driver of targets) {
        const phone = driver.mobile;
        if (channel === 'sms' || channel === 'whatsapp') {
          if (!phone) continue;
          const res = await fetch('/.netlify/functions/send-sms', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: phone,
              message,
              whatsapp: channel === 'whatsapp',
            }),
          });
          const data = await res.json();
          if (data.success || data.sid) ok++;
        } else if (channel === 'email') {
          if (!driver.email) continue;
          const res = await fetch('/.netlify/functions/appsscript-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: driver.email,
              subject: `HOLLYPAUL Driver Portal Guide — ${guide.lang}`,
              body: `Hi ${driver.name || driver.loginName},\n\nYour Driver Portal getting started guide (${guide.lang}) is now available.\n\nDownload it here: ${guideUrl}\n\nYou can log in at: https://hollypaul.com/driver-log\n\nIf you have any questions, contact your manager.\n\nHollypaul`,
            }),
          });
          const data = await res.json();
          if (data.success) ok++;
        }
      }
      addNotification(`Sent to ${ok} of ${selected.length} drivers`, 'success');
      onClose();
    } catch (err) {
      addNotification(`Send failed: ${err.message}`, 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius-lg)', padding: 28, width: 480, maxWidth: '95vw', maxHeight: '85vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1rem' }}>Send Guide — {guide.flag} {guide.lang}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', marginTop: 2 }}>Select drivers and delivery channel</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--hp-dim)' }}><X size={18} /></button>
        </div>

        {/* Channel selector */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {[
            { key: 'sms',      icon: <Smartphone size={13} />, label: 'SMS' },
            { key: 'whatsapp', icon: <MessageCircle size={13} />, label: 'WhatsApp' },
            { key: 'email',    icon: <Mail size={13} />, label: 'Email' },
          ].map(ch => (
            <button key={ch.key} onClick={() => setChannel(ch.key)}
              className={`btn btn-sm ${channel === ch.key ? 'btn-primary' : 'btn-secondary'}`}
              style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              {ch.icon} {ch.label}
            </button>
          ))}
        </div>

        {/* Driver search + select */}
        <div style={{ marginBottom: 10, display: 'flex', gap: 8 }}>
          <input className="form-input" placeholder="Search drivers…" value={filterText}
            onChange={e => setFilterText(e.target.value)} style={{ flex: 1, fontSize: '0.8rem' }} />
          <button className="btn btn-secondary btn-sm" onClick={selectAll}>All</button>
          <button className="btn btn-secondary btn-sm" onClick={clearAll}>Clear</button>
        </div>

        <div style={{ border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', maxHeight: 220, overflow: 'auto', marginBottom: 16 }}>
          {filteredDrivers.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: 'var(--hp-dim)', fontSize: '0.8rem' }}>No drivers found</div>
          ) : filteredDrivers.map(d => (
            <label key={d.loginName} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--hp-border)', background: selected.includes(d.loginName) ? 'rgba(201,168,76,0.06)' : 'none' }}>
              <input type="checkbox" checked={selected.includes(d.loginName)} onChange={() => toggle(d.loginName)} />
              <div>
                <div style={{ fontSize: '0.82rem', fontWeight: 600 }}>{d.name || d.loginName}</div>
                <div style={{ fontSize: '0.7rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)' }}>
                  {d.loginName} · {channel === 'email' ? (d.email || 'No email') : (d.mobile || 'No mobile')}
                </div>
              </div>
            </label>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '0.78rem', color: 'var(--hp-dim)' }}>{selected.length} driver{selected.length !== 1 ? 's' : ''} selected</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={send} disabled={sending || !selected.length}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Send size={13} /> {sending ? 'Sending…' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Driver Guides Section ─────────────────────────────────────────────────────
function DriverGuidesSection({ drivers, addNotification }) {
  const [expanded, setExpanded]    = useState(true);
  const [sendModal, setSendModal]  = useState(null);

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', marginBottom: expanded ? 16 : 0 }}
        onClick={() => setExpanded(e => !e)}>
        <div>
          <div style={{ fontWeight: 700, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Globe size={16} color="var(--hp-gold)" /> Driver Portal — Getting Started Guides
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', marginTop: 2 }}>
            4 languages · English · Spanish · Mandarin · Mongolian
          </div>
        </div>
        {expanded ? <ChevronUp size={16} color="var(--hp-dim)" /> : <ChevronDown size={16} color="var(--hp-dim)" />}
      </div>

      {expanded && (
        <>
        {/* Interactive guide link */}
        <div style={{ marginBottom: 14, padding: '14px 16px', background: 'rgba(201,168,76,0.07)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 'var(--radius)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--hp-text)' }}>📱 Interactive Driver Guide</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', marginTop: 2 }}>Step-by-step walkthrough — share this link directly with drivers</div>
          </div>
          <a href="/driver-guide.html" target="_blank" rel="noreferrer"
            className="btn btn-primary btn-sm"
            style={{ textDecoration: 'none', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 5 }}>
            <FileText size={12} /> Open Guide
          </a>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(220px, 100%), 1fr))', gap: 14 }}>
          {DRIVER_GUIDES.map(g => (
            <div key={g.code} style={{ border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '16px', background: 'var(--hp-bg)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <span style={{ fontSize: '1.6rem' }}>{g.flag}</span>
                <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{g.lang}</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  <a href={g.file} target="_blank" rel="noreferrer"
                    className="btn btn-secondary btn-sm"
                    style={{ display: 'flex', alignItems: 'center', gap: 5, textDecoration: 'none', flex: 1, justifyContent: 'center' }}>
                    <FileText size={12} /> View
                  </a>
                  <a href={g.file} download
                    className="btn btn-secondary btn-sm"
                    style={{ display: 'flex', alignItems: 'center', gap: 5, textDecoration: 'none', flex: 1, justifyContent: 'center' }}>
                    <Download size={12} /> Save
                  </a>
                </div>
                <button className="btn btn-primary btn-sm"
                  style={{ display: 'flex', alignItems: 'center', gap: 5, width: '100%', justifyContent: 'center' }}
                  onClick={() => setSendModal(g)}>
                  <Send size={12} /> Send to Drivers
                </button>
              </div>
            </div>
          ))}
        </div>
        </>
      )}

      {sendModal && (
        <SendModal
          guide={sendModal}
          drivers={drivers}
          onClose={() => setSendModal(null)}
          addNotification={addNotification}
        />
      )}
    </div>
  );
}

// ── Delivery Procedures — driver read-receipts report ─────────────────────────
// Who has opened each procedure (Knock & Call Out, DCA manual, …) and when.
// Reads the same view log the portal's Delivery Procedures tab stamps.
const DP_API = '/.netlify/functions/delivery-procedures-sb';

function fmtWhen(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
}

function ProcedureViewsReport({ drivers }) {
  const [data, setData]     = useState(null);   // { items, views } | null
  const [error, setError]   = useState('');
  const [openId, setOpenId] = useState('');

  const nameFor = useCallback((login) => {
    const up = (login || '').toUpperCase();
    const d = (drivers || []).find(x => (x.loginName || '').toUpperCase() === up);
    return d?.name || login;
  }, [drivers]);

  const load = useCallback(async () => {
    try {
      const res = await fetch(DP_API, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'report' }),
      });
      const d = await res.json();
      if (d && d.success) setData({ items: d.items || [], views: d.views || [] });
      else setError(d?.error || 'Could not load report');
    } catch (e) { setError(e.message); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (error) return <div style={{ fontSize: '0.78rem', color: 'var(--hp-red)', marginTop: 12 }}>{error}</div>;
  if (!data) return <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginTop: 12 }}>Loading read-receipts…</div>;

  // Driver-facing items only (the shelf drivers actually see).
  const items = (data.items || []).filter(it => it.showToDrivers !== false);

  return (
    <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px dashed var(--hp-border)' }}>
      <div style={{ fontWeight: 700, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4 }}>
        <Eye size={14} color="var(--hp-gold)" /> Driver read-receipts
      </div>
      <div style={{ fontSize: '0.74rem', color: 'var(--hp-dim)', marginBottom: 12 }}>
        Who has opened each procedure in the portal, and when. Tap a row to see the drivers.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map(it => {
          const rows = (data.views || []).filter(v => v.docId === it.id)
            .sort((a, b) => (b.last || '').localeCompare(a.last || ''));
          const open = openId === it.id;
          return (
            <div key={it.id} style={{ border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
              <button onClick={() => setOpenId(open ? '' : it.id)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--hp-text)' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: '0.84rem' }}>
                  {it.kind === 'guide' ? <BookOpen size={14} color="var(--hp-gold)" /> : <FileText size={14} color="var(--hp-gold)" />}
                  {it.title}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className="badge" style={{ background: rows.length ? 'rgba(34,197,94,0.12)' : 'var(--hp-bg)', color: rows.length ? 'var(--hp-green)' : 'var(--hp-dim)', border: '1px solid var(--hp-border)' }}>
                    {rows.length} opened
                  </span>
                  {open ? <ChevronUp size={14} color="var(--hp-dim)" /> : <ChevronDown size={14} color="var(--hp-dim)" />}
                </span>
              </button>
              {open && (
                <div style={{ padding: '4px 14px 12px', borderTop: '1px solid var(--hp-border)' }}>
                  {rows.length === 0 ? (
                    <div style={{ fontSize: '0.76rem', color: 'var(--hp-dim)', padding: '8px 0' }}>No driver has opened this yet.</div>
                  ) : rows.map(r => (
                    <div key={r.login} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '6px 0', fontSize: '0.78rem', borderBottom: '1px solid var(--hp-border)' }}>
                      <span style={{ fontWeight: 600 }}>{nameFor(r.login)}
                        <span style={{ color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)', fontWeight: 400, marginLeft: 6, fontSize: '0.72rem' }}>{r.login}</span>
                      </span>
                      <span style={{ color: 'var(--hp-dim)' }}>{fmtWhen(r.last)}{r.count > 1 ? ` · ${r.count}×` : ''}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function DocumentsPage() {
  const { documents, addNotification, sheetData } = useApp();
  const [category, setCategory] = useState('All');
  const [search, setSearch]     = useState('');

  const drivers = sheetData?.drivers || [];

  const filtered = documents.filter(d => {
    const matchCat    = category === 'All' || d.category === category;
    const matchSearch = d.title.toLowerCase().includes(search.toLowerCase()) || d.category.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const currentCount    = documents.filter(d => d.status === 'current').length;
  const reviewCount     = documents.filter(d => d.status === 'review').length;
  const expiredCount    = documents.filter(d => d.status === 'expired').length;
  const compliancePct   = documents.length > 0 ? Math.round((currentCount / documents.length) * 100) : 0;

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <h2>Documents</h2>
          <p>Compliance document management · driver guides · {documents.length} documents</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn btn-secondary" onClick={() => addNotification('Upload document — coming soon')}>
            <Upload size={14} /> Upload
          </button>
          <button className="btn btn-primary" onClick={() => addNotification('Create document — coming soon')}>
            <Plus size={14} /> New Document
          </button>
        </div>
      </div>

      <div className="page-body">

        {/* Driver Documents — push to a driver's My Docs tab */}
        <DriverDocsManager drivers={drivers} addNotification={addNotification} />

        {/* Statements — generate a branded statement from engine data & push it */}
        <LeaseStatementBuilder drivers={drivers} addNotification={addNotification} />

        {/* Fleet Communications */}
        <div className="card" style={{ marginBottom: 24 }}>
          <div style={{ fontWeight: 700, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <FileText size={16} color="var(--hp-gold)" /> Fleet Communications
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '14px 16px', background: 'rgba(201,168,76,0.07)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 'var(--radius)' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--hp-text)' }}>Why We Are Going Digital</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', marginTop: 2 }}>
                Letter to the fleet — MSO &amp; RTCCO compliance, Driver Portal introduction · June 2026
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <a href="/docs/digital-transition-letter.pdf" target="_blank" rel="noreferrer"
                className="btn btn-secondary btn-sm"
                style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5 }}>
                <FileText size={12} /> View
              </a>
              <a href="/docs/digital-transition-letter.pdf" download="Hollypaul-Digital-Transition-Letter.pdf"
                className="btn btn-primary btn-sm"
                style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5 }}>
                <Download size={12} /> Download
              </a>
            </div>
          </div>
        </div>

        {/* Training & Standards — illustrated guides + AP source documents */}
        <div className="card" style={{ marginBottom: 24 }}>
          <div style={{ fontWeight: 700, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <FileText size={16} color="var(--hp-gold)" /> Training &amp; Standards — Delivery
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginBottom: 14 }}>
            The AP delivery standards that govern our run — and a plain-English illustrated version for drivers.
          </div>

          {/* Illustrated Knock & Call Out guide */}
          <div style={{ marginBottom: 14, padding: '14px 16px', background: 'rgba(201,168,76,0.07)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 'var(--radius)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--hp-text)' }}>🚪 Knock &amp; Call Out — Illustrated Guide</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', marginTop: 2 }}>Comic-style walkthrough of the "knock three times" &amp; Safe Drop process — share with drivers</div>
            </div>
            <a href="/knock-and-call-out.html" target="_blank" rel="noreferrer"
              className="btn btn-primary btn-sm"
              style={{ textDecoration: 'none', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 5 }}>
              <FileText size={12} /> Open Guide
            </a>
          </div>

          {/* Illustrated Safe Drop guide */}
          <div style={{ marginBottom: 14, padding: '14px 16px', background: 'rgba(201,168,76,0.07)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 'var(--radius)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--hp-text)' }}>📦 Safe Drop — Illustrated Guide</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', marginTop: 2 }}>When you can leave a parcel, the secure-spot &amp; photo rules, and the Alcohol / RSA (NSW) rules</div>
            </div>
            <a href="/safe-drop.html" target="_blank" rel="noreferrer"
              className="btn btn-primary btn-sm"
              style={{ textDecoration: 'none', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 5 }}>
              <FileText size={12} /> Open Guide
            </a>
          </div>

          {/* Illustrated Verbal Drop guide */}
          <div style={{ marginBottom: 14, padding: '14px 16px', background: 'rgba(201,168,76,0.07)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 'var(--radius)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--hp-text)' }}>🔔 Verbal Drop — Illustrated Guide</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', marginTop: 2 }}>Signature items accepted by voice over intercom / smart doorbell — the script, name capture, when NOT to use it</div>
            </div>
            <a href="/verbal-drop.html" target="_blank" rel="noreferrer"
              className="btn btn-primary btn-sm"
              style={{ textDecoration: 'none', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 5 }}>
              <FileText size={12} /> Open Guide
            </a>
          </div>

          {/* Illustrated Dealing With Dogs guide */}
          <div style={{ marginBottom: 14, padding: '14px 16px', background: 'rgba(201,168,76,0.07)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 'var(--radius)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--hp-text)' }}>🐕 Dealing With Dogs — Illustrated Guide</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', marginTop: 2 }}>Animal-attack critical controls &amp; life-saving behaviours — dog register, rattle the gate, keep your distance, what to do if a dog comes at you</div>
            </div>
            <a href="/dealing-with-dogs.html" target="_blank" rel="noreferrer"
              className="btn btn-primary btn-sm"
              style={{ textDecoration: 'none', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 5 }}>
              <FileText size={12} /> Open Guide
            </a>
          </div>

          {/* Illustrated Delivered Location guide + practice scanner */}
          <div style={{ marginBottom: 14, padding: '14px 16px', background: 'rgba(201,168,76,0.07)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 'var(--radius)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--hp-text)' }}>🏢 Delivered Location — Illustrated Guide</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', marginTop: 2 }}>The scanner drop-down the customer actually reads — letterbox, concierge/reception, mailroom staff — and why a typed comment never reaches them</div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <a href="/delivered-location-practice.html" target="_blank" rel="noreferrer"
                className="btn btn-secondary btn-sm"
                style={{ textDecoration: 'none', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 5 }}>
                ▶ Practice
              </a>
              <a href="/delivered-location.html" target="_blank" rel="noreferrer"
                className="btn btn-primary btn-sm"
                style={{ textDecoration: 'none', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 5 }}>
                <FileText size={12} /> Open Guide
              </a>
            </div>
          </div>

          {/* Source documents */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '12px 16px', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', marginBottom: 10 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--hp-text)' }}>DCA Operations Manual</div>
              <div style={{ fontSize: '0.74rem', color: 'var(--hp-dim)', marginTop: 2 }}>
                Operations Manual for the Delivery Contractor Agreement · Australia Post · November 2023
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <a href="/docs/dca-operations-manual-2023.pdf" target="_blank" rel="noreferrer"
                className="btn btn-secondary btn-sm"
                style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5 }}>
                <FileText size={12} /> View
              </a>
              <a href="/docs/dca-operations-manual-2023.pdf" download="DCA-Operations-Manual-Nov-2023.pdf"
                className="btn btn-primary btn-sm"
                style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5 }}>
                <Download size={12} /> Download
              </a>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '12px 16px', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', marginBottom: 10 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--hp-text)' }}>Deliver Article — Quick Reference Guide</div>
              <div style={{ fontSize: '0.74rem', color: 'var(--hp-dim)', marginTop: 2 }}>
                Australia Post QRG · V01 · 1 November 2023 · the Task List flow and the Delivery Location drop-down, screen by screen
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <a href="/docs/deliver-article-qrg-2023.pdf" target="_blank" rel="noreferrer"
                className="btn btn-secondary btn-sm"
                style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5 }}>
                <FileText size={12} /> View
              </a>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '12px 16px', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', marginBottom: 10 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--hp-text)' }}>Additional Delivered Location Events</div>
              <div style={{ fontSize: '0.74rem', color: 'var(--hp-dim)', marginTop: 2 }}>
                Australia Post Toolbox Talk · 23 November 2022 · source for the Delivered Location guide
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <a href="/docs/additional-delivered-locations-toolbox-talk-2022.pdf" target="_blank" rel="noreferrer"
                className="btn btn-secondary btn-sm"
                style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5 }}>
                <FileText size={12} /> View
              </a>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '12px 16px', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--hp-text)' }}>Knock &amp; Call Out — Original AP Advice</div>
              <div style={{ fontSize: '0.74rem', color: 'var(--hp-dim)', marginTop: 2 }}>
                Australia Post operational advice, original illustrated bulletin · 2015 · reference
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <a href="/docs/knock-and-call-out-original-2015.pdf" target="_blank" rel="noreferrer"
                className="btn btn-secondary btn-sm"
                style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5 }}>
                <FileText size={12} /> View
              </a>
            </div>
          </div>

          {/* Read-receipts — who's opened each procedure in the portal */}
          <ProcedureViewsReport drivers={drivers} />
        </div>

        {/* Applicant Onboarding Guide ("Next Steps") */}
        <div className="card" style={{ marginBottom: 24 }}>
          <div style={{ fontWeight: 700, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <FileText size={16} color="var(--hp-gold)" /> Applicant Onboarding Guide — "Next Steps"
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginBottom: 14 }}>
            Hand to applicants after interview — Police / Fit2Work check through to first self-managed loads · v22.
            {' '}<span style={{ color: 'var(--hp-muted)' }}>English is the version the Applicants → Next Steps email &amp; WhatsApp buttons send.</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(220px, 100%), 1fr))', gap: 14 }}>
            {[
              { lang: 'English',   flag: '🇦🇺', file: '/docs/onboarding-guide-en.pdf' },
              { lang: 'Spanish',   flag: '🇪🇸', file: '/docs/onboarding-guide-es.pdf' },
              { lang: 'Mandarin',  flag: '🇨🇳', file: '/docs/onboarding-guide-zh.pdf' },
              { lang: 'Mongolian', flag: '🇲🇳', file: '/docs/onboarding-guide-mn.pdf' },
            ].map(g => (
              <div key={g.lang} style={{ border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '16px', background: 'var(--hp-bg)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <span style={{ fontSize: '1.6rem' }}>{g.flag}</span>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{g.lang}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <a href={g.file} target="_blank" rel="noreferrer"
                      className="btn btn-secondary btn-sm"
                      style={{ display: 'flex', alignItems: 'center', gap: 5, textDecoration: 'none', flex: 1, justifyContent: 'center' }}>
                      <FileText size={12} /> View
                    </a>
                    <a href={g.file} download
                      className="btn btn-secondary btn-sm"
                      style={{ display: 'flex', alignItems: 'center', gap: 5, textDecoration: 'none', flex: 1, justifyContent: 'center' }}>
                      <Download size={12} /> Save
                    </a>
                  </div>
                  <button className="btn btn-primary btn-sm"
                    style={{ display: 'flex', alignItems: 'center', gap: 5, width: '100%', justifyContent: 'center' }}
                    onClick={() => {
                      const url = `https://hollypaul.com${g.file}`;
                      if (navigator.clipboard) {
                        navigator.clipboard.writeText(url).then(
                          () => addNotification(`${g.lang} guide link copied`, 'success'),
                          () => addNotification(url, 'info')
                        );
                      } else {
                        addNotification(url, 'info');
                      }
                    }}>
                    <Send size={12} /> Copy link
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Driver Guides */}
        <DriverGuidesSection drivers={drivers} addNotification={addNotification} />

        {/* Stats */}
        <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)', marginBottom: '24px' }}>
          {[
            { label: 'Total Documents', value: documents.length,  accent: 'var(--hp-gold)' },
            { label: 'Current',         value: currentCount,      accent: 'var(--hp-green)' },
            { label: 'Due for Review',  value: reviewCount,       accent: 'var(--hp-amber)' },
            { label: 'Expired',         value: expiredCount,      accent: expiredCount > 0 ? 'var(--hp-red)' : 'var(--hp-green)' },
          ].map(s => (
            <div className="stat-card" key={s.label} style={{ '--accent': s.accent }}>
              <div className="stat-label">{s.label}</div>
              <div className="stat-value" style={{ fontSize: '2rem' }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Compliance health */}
        <div className="card" style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <div>
              <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>Document Compliance Health</span>
              <span style={{ marginLeft: '12px', fontSize: '0.8rem', color: 'var(--hp-dim)' }}>{compliancePct}% current</span>
            </div>
            {expiredCount > 0 && <span className="badge badge-red">{expiredCount} expired — action required</span>}
          </div>
          <div className="compliance-bar">
            <div className="compliance-fill" style={{ width: `${compliancePct}%`, background: compliancePct === 100 ? 'var(--hp-green)' : 'var(--hp-gold)' }} />
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', alignItems: 'center' }}>
          <div style={{ position: 'relative', maxWidth: '260px', flex: 1 }}>
            <Search size={14} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--hp-dim)' }} />
            <input className="form-input" style={{ paddingLeft: '36px' }} placeholder="Search documents..."
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {CATEGORIES.map(c => (
              <button key={c} className={`btn btn-sm ${category === c ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setCategory(c)}>{c}</button>
            ))}
          </div>
        </div>

        {/* Document cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(340px, 100%), 1fr))', gap: '16px' }}>
          {filtered.map(doc => {
            const si = STATUS_INFO[doc.status];
            const StatusIcon = si.icon;
            const days = daysUntil(doc.review_due);
            return (
              <div key={doc.id} className="card"
                style={{ border: doc.status === 'expired' ? '1px solid rgba(239,68,68,0.3)' : doc.status === 'review' ? '1px solid rgba(201,168,76,0.3)' : '1px solid var(--hp-border)', cursor: 'pointer', transition: 'all var(--transition)' }}
                onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                onClick={() => addNotification(`Opening: ${doc.title}`)}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: 40, height: 40, background: `rgba(${doc.status === 'expired' ? '239,68,68' : doc.status === 'review' ? '201,168,76' : '34,197,94'},0.08)`, border: `1px solid rgba(${doc.status === 'expired' ? '239,68,68' : doc.status === 'review' ? '201,168,76' : '34,197,94'},0.2)`, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FileText size={16} color={si.color} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.875rem', lineHeight: 1.3 }}>{doc.title}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>{doc.category} · v{doc.version}</div>
                    </div>
                  </div>
                  <span className={`badge ${si.badge}`} style={{ flexShrink: 0 }}>{si.label}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 14, borderTop: '1px solid var(--hp-border)' }}>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--hp-muted)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Review Due</div>
                    <div style={{ fontSize: '0.8rem', color: days < 0 ? 'var(--hp-red)' : days < 30 ? 'var(--hp-amber)' : 'var(--hp-dim)', fontWeight: days < 30 ? 600 : 400, marginTop: 2 }}>
                      {new Date(doc.review_due).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}
                      {days < 0 && <span style={{ marginLeft: 6, fontSize: '0.72rem' }}>OVERDUE</span>}
                      {days >= 0 && days < 60 && <span style={{ marginLeft: 6, fontSize: '0.72rem', color: 'var(--hp-muted)' }}>({days}d)</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-ghost btn-sm" onClick={e => { e.stopPropagation(); addNotification(`Download: ${doc.title}`, 'success'); }}>Download</button>
                    {doc.status !== 'current' && (
                      <button className="btn btn-primary btn-sm" onClick={e => { e.stopPropagation(); addNotification(`Mark current: ${doc.title}`, 'success'); }}>Update</button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
